import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plane, CheckCircle, XCircle, MessageSquare, Save, Plus, Pencil, Trash } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { usePromptContext } from '@/contexts/PromptContext';
import { OpenAIPrompt } from '@/lib/supabase/prompts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState('openai');
  const [isTestingOpenAiApi, setIsTestingOpenAiApi] = React.useState<boolean>(false);
  const [openaiApiTestResult, setOpenaiApiTestResult] = React.useState<string | null>(null);
  const { isConfigured, testConnection } = useOpenAiApi();
  
  // Use the prompt context instead of the hook
  const { prompts, isLoading, error, refreshPrompts, updatePrompt } = usePromptContext();
  
  // Estados para o diálogo de edição/criação de prompt
  const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<OpenAIPrompt | null>(null);
  const [promptName, setPromptName] = useState('');
  const [promptText, setPromptText] = useState('');

  // Load prompts on initial mount
  useEffect(() => {
    refreshPrompts();
  }, []);

  const handleTestOpenAiApi = async () => {
    setIsTestingOpenAiApi(true);
    setOpenaiApiTestResult(null);

    try {
      // Use the testConnection function
      await testConnection();
      setOpenaiApiTestResult('success');
      toast({
        title: 'Conexão bem-sucedida',
        description: 'A conexão com a API da OpenAI foi estabelecida com sucesso.',
      });
    } catch (error) {
      setOpenaiApiTestResult('error');
      toast({
        title: 'Erro na conexão',
        description: error instanceof Error ? error.message : 'Ocorreu um erro ao testar a conexão com a API da OpenAI.',
        variant: 'destructive',
      });
    } finally {
      setIsTestingOpenAiApi(false);
    }
  };

  const openPromptDialog = (prompt?: OpenAIPrompt) => {
    if (prompt) {
      // Editar prompt existente
      setEditingPrompt(prompt);
      setPromptName(prompt.name);
      setPromptText(prompt.promptText);
    } else {
      // Novo prompt
      setEditingPrompt(null);
      setPromptName('');
      setPromptText('');
    }
    setIsPromptDialogOpen(true);
  };

  const handleSavePromptDialog = async () => {
    if (!promptName.trim()) {
      toast({
        title: "Nome não informado",
        description: "Por favor, insira um nome para o prompt.",
        variant: "destructive",
      });
      return;
    }

    if (!promptText.trim()) {
      toast({
        title: "Prompt não informado",
        description: "Por favor, insira o conteúdo do prompt.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingPrompt) {
        // Update existing prompt
        await updatePrompt(editingPrompt.id, {
          name: promptName.trim(),
          promptText: promptText.trim()
        });
        
        toast({
          title: "Prompt atualizado",
          description: `O prompt "${promptName}" foi atualizado com sucesso.`
        });
      } else {
        // Create new prompt through the service (context doesn't have create method)
        const promptsService = await import('@/lib/supabase/prompts').then(m => m.promptsService);
        await promptsService.createPrompt({
          name: promptName.trim(),
          promptText: promptText.trim(),
          isDefault: false,
          description: '',
          category: 'User Created'
        });
        
        toast({
          title: "Prompt criado",
          description: `O prompt "${promptName}" foi criado com sucesso.`
        });
      }
      
      // Close dialog and refresh prompts
      setIsPromptDialogOpen(false);
      refreshPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar o prompt. Por favor, tente novamente.",
        variant: "destructive"
      });
    }
  };

  const handleDeletePrompt = async (promptId: string, promptName: string) => {
    try {
      // Delete through the service (context doesn't have delete method)
      const promptsService = await import('@/lib/supabase/prompts').then(m => m.promptsService);
      await promptsService.deletePrompt(promptId);
      
      toast({
        title: "Prompt excluído",
        description: `O prompt "${promptName}" foi excluído com sucesso.`
      });
      
      // Refresh prompts
      refreshPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast({
        title: "Erro ao excluir",
        description: "Ocorreu um erro ao excluir o prompt. Por favor, tente novamente.",
        variant: "destructive"
      });
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <h1 className="text-2xl font-bold mb-6">Configurações</h1>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="openai">Prompts OpenAI</TabsTrigger>
          </TabsList>
          
          <TabsContent value="openai">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>API da OpenAI</CardTitle>
                <CardDescription>
                  Teste a conexão com a API da OpenAI utilizando a chave configurada no arquivo .env
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  A chave da API da OpenAI está configurada através de variáveis de ambiente (.env).
                  Se você precisar alterar a chave, atualize o arquivo .env e reinicie a aplicação.
                </p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <div className="flex items-center space-x-2">
                  {openaiApiTestResult === 'success' && (
                    <div className="flex items-center text-green-500">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      <span>Conexão bem-sucedida</span>
                    </div>
                  )}
                  {openaiApiTestResult === 'error' && (
                    <div className="flex items-center text-red-500">
                      <XCircle className="h-4 w-4 mr-1" />
                      <span>Falha na conexão</span>
                    </div>
                  )}
                </div>
                  <Button 
                    variant="outline" 
                    onClick={handleTestOpenAiApi}
                  disabled={isTestingOpenAiApi}
                  >
                    {isTestingOpenAiApi ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Testando...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Testar Conexão
                      </>
                    )}
                  </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Gerenciamento de Prompts</CardTitle>
                  <CardDescription>
                    Configure os prompts que serão utilizados pela OpenAI nas funcionalidades da aplicação.
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => openPromptDialog()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Prompt
                </Button>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : Object.keys(prompts).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum prompt configurado. Clique em "Novo Prompt" para adicionar.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.values(prompts).filter(Boolean).map((prompt) => (
                      <div 
                        key={prompt!.id} 
                        className="border rounded-md p-4 hover:border-gray-400 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium">{prompt!.name}</h3>
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openPromptDialog(prompt!)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Trash className="h-4 w-4 text-red-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir prompt</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir o prompt "{prompt!.name}"? Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeletePrompt(prompt!.id, prompt!.name)}>
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{prompt!.promptText.substring(0, 200)}{prompt!.promptText.length > 200 ? '...' : ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog for creating/editing prompts */}
      <Dialog open={isPromptDialogOpen} onOpenChange={setIsPromptDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPrompt ? 'Editar Prompt' : 'Novo Prompt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="promptName">Nome do Prompt</Label>
              <Input
                id="promptName"
                value={promptName}
                onChange={(e) => setPromptName(e.target.value)}
                placeholder="Nome descritivo para o prompt"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promptText">Conteúdo do Prompt</Label>
              <Textarea
                id="promptText"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="Conteúdo do prompt para a OpenAI"
                className="min-h-[200px]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {'{placeholders}'} para variáveis que serão substituídas automaticamente.
                Por exemplo: {'{tourName}'} será substituído pelo nome do passeio.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPromptDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePromptDialog}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
} 